// @ts-nocheck
// Planner core: one pure module for every planning path (preview, first week, day/week remix,
// swap ranking, Überrasch mich, portions, swipe deck order). No DOM, no global state, no Math.random.
//
// Day-level optimization: a shortlist per open meal, every combination scored, portions solved per
// combination (¼ steps, 0.5–2×, share cap hard), seeded pick among the best few days.
// One normalized score (lower is better; every term is relative to the target):
//   nutrition (kcal, protein shortfall, carbs/fat, per-meal share) + taste (likes, skips, cook time,
//   popularity) + variety (repeats, main ingredient) + practicality (portions near 1×).
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.vhpPlannerCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const STEPS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  const SHORTLIST = { main: 6, other: 4 };
  const SHORTLIST_LIKES = 2;
  const SHORTLIST_EXPLORE = 2;
  const KEEP_DAYS = 5;
  const MAIN_MAX = 2;

  const WEIGHTS = {
    kcalTol: 0.03, // free band around the kcal target
    kcalQuad: 200, // (relative error beyond the band)²
    kcalLin: 2,
    proteinLin: 6, // shortfall only; over is fine
    proteinTol: 0.05,
    proteinQuad: 300,
    carbs: 1,
    fat: 1,
    share: 1, // per meal: |kcal − share × target| / target
    portion: 0.15, // per meal: |scale − 1|
    likeMain: 0.3,
    likeOther: 0.6, // breakfast / snack: likes matter more there
    skip: 0.4,
    timeOver: 0.3, // over maxTime, plus timePerMin per minute over
    timePerMin: 0.01,
    popularity: 1.5, // × (likeRate − avg) and × (swapRate − avg)
    mainRepeat: 0.5, // second use of a main in the week
    mainRecent: 0.35, // main that is also in the neighbouring week (last week, or this week when planning next)
    prepDinner: 0.35, // Reste einplanen: a dinner that feeds tomorrow's lunch should reheat well (Meal-Prep recipes)
    mainOverCap: 10, // per use beyond MAIN_MAX (only when nothing else fits)
    mainAdjacent: 0.5, // same main on the day before / after
    otherYesterday: 0.25, // breakfast / snack: same recipe the day before, halving per day further back
    ingredientSameDay: 0.4,
    ingredientAdjacent: 0.15,
    overShare: 5, // a portion that breaks the share cap even at 0.5×
  };

  // Favoriten sheet. mix scales every like (and how many likes get a guaranteed shortlist seat);
  // a favorite set to "often" counts twice. Paused favorites never reach the core (the caller drops them).
  const FAV_MIX = {
    more: { like: 2, shortlist: 3 },
    balanced: { like: 1, shortlist: SHORTLIST_LIKES },
    new: { like: 0.35, shortlist: 1 },
  };
  const FREQ_WEIGHT = { often: 2, normal: 1 };

  const DAY_PICK = { band: 0.25, temperature: 0.1 };
  // Remix: repeated taps should feel varied, so the seeded pick reaches a little further from the best day.
  const REMIX_PICK = { band: 0.5, temperature: 0.25 };
  const SURPRISE_PICK = { top: 8, temperature: 0.3 };

  // ---------- ids, seeds ----------

  // Product id digits, or `c<id>` for a custom recipe.
  const productId = (id) => {
    const match = String(id ?? '').match(/(c?\d+)$/);
    return match ? match[1] : '';
  };
  const isCustomId = (id) => /^c\d+$/.test(String(id ?? ''));

  // FNV-1a over the joined parts → uint32.
  const seed = (...parts) => {
    const text = parts.map((part) => String(part ?? '')).join('|');
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  };

  // mulberry32 → () => [0, 1)
  const rng = (s) => {
    let a = s >>> 0;
    return () => {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const softmaxPick = (rows, scoreOf, temperature, rand) => {
    if (!rows.length) return null;
    const best = Math.min(...rows.map(scoreOf));
    const weights = rows.map((row) => Math.exp(-(scoreOf(row) - best) / temperature));
    let roll = rand() * weights.reduce((sum, w) => sum + w, 0);
    for (let i = 0; i < rows.length; i += 1) {
      roll -= weights[i];
      if (roll <= 0) return rows[i];
    }
    return rows[rows.length - 1];
  };

  // ---------- context ----------

  const num = (value) => (value == null || value === '' ? NaN : Number(value));
  const hasMacros = (r) =>
    num(r?.kcal) > 0 && Number.isFinite(num(r?.protein)) && Number.isFinite(num(r?.carbs)) && Number.isFinite(num(r?.fat));

  const toRecipe = (card, slot) => ({
    id: productId(card.id),
    slot: card.slot || slot,
    kcal: Number(card.kcal),
    protein: Number(card.protein),
    carbs: Number(card.carbs),
    fat: Number(card.fat),
    time: Number.isFinite(num(card.time)) ? Number(card.time) : null,
    mi: card.mi || null,
    pub: card.pub || null,
    card,
  });

  const toIdSet = (list) => new Set([...(list || [])].map(productId).filter(Boolean));

  // Inputs are already filtered by the caller: exclusions (likes included) and plannable cards only.
  // Cards without full macros are dropped here as a second line.
  const context = (opts) => {
    const target = {
      kcal: Number(opts.target?.kcal) || 0,
      protein: Number(opts.target?.protein) || 0,
      carbs: Number(opts.target?.carbs) || 0,
      fat: Number(opts.target?.fat) || 0,
    };
    const catalog = {};
    Object.keys(opts.catalog || {}).forEach((slot) => {
      const seen = new Set();
      catalog[slot] = (opts.catalog[slot] || [])
        .filter(hasMacros)
        .map((card) => toRecipe(card, slot))
        .filter((r) => r.id && !seen.has(r.id) && seen.add(r.id));
    });
    const occasions = (opts.occasions || []).map((row) => ({
      id: row.id,
      slot: row.slot || row.catalog,
      share: Number(row.share) || 0,
    }));
    return {
      target,
      occasions,
      shareCap: Number(opts.shareCap) > 0 ? Number(opts.shareCap) : 0.4,
      maxTime: Number(opts.maxTime) > 0 ? Number(opts.maxTime) : null,
      catalog,
      likes: toIdSet(opts.likes),
      skips: toIdSet(opts.skips),
      recent: toIdSet(opts.recent),
      prep: toIdSet(opts.prep),
      fav: FAV_MIX[opts.mix] || FAV_MIX.balanced,
      likeWeight: new Map(
        Object.entries(opts.freq || {})
          .map(([id, freq]) => [productId(id), FREQ_WEIGHT[freq] || 1])
          .filter(([id]) => id)
      ),
      stats: opts.stats && opts.stats.r ? opts.stats : null,
      W: { ...WEIGHTS, ...(opts.weights || {}) },
    };
  };

  const shareLimit = (ctx) => Math.floor(ctx.target.kcal * ctx.shareCap);

  // Portionsgröße per meal (Einstellungen): s / m / l weights the meal's default share, then all shares
  // are normalized to 1. Default sizes (all m) leave the shares as they were.
  const MEAL_SIZES = { s: 0.6, m: 1, l: 1.5 };
  const mealShares = (occasions, sizes) => {
    const rows = (occasions || []).map((row) => ({
      ...row,
      share: (Number(row.share) || 0) * (MEAL_SIZES[sizes?.[row.id]] || 1),
    }));
    const total = rows.reduce((sum, row) => sum + row.share, 0) || 1;
    return rows.map((row) => ({ ...row, share: row.share / total }));
  };
  // Mahlzeiten screen: how many recipes of a slot can be portioned (½× to 2×, under the single-meal cap) to
  // within ±12 % of a meal's kcal. A small count means the week repeats; 0 means the meal can't be planned well.
  const FIT_TOLERANCE = 0.12;
  const fitCount = (cards, mealKcal, limit) => {
    const target = Number(mealKcal);
    if (!(target > 0)) return 0;
    const ids = new Set();
    (cards || []).forEach((card) => {
      const kcal = num(card?.kcal);
      if (!(kcal > 0)) return;
      const fits = STEPS.some((s) => {
        const shown = kcal * s;
        return !(limit > 0 && shown > limit) && Math.abs(shown - target) <= target * FIT_TOLERANCE;
      });
      if (fits) ids.add(productId(card.id) || card);
    });
    return ids.size;
  };
  // Single-meal kcal cap: 40 %, 55 % once a meal's share is over 40 %, and just above the share when a
  // big meal (e.g. Groß with only lunch + dinner) is over 55 % on its own.
  const shareCapFor = (occasions) => {
    const max = Math.max(0, ...(occasions || []).map((row) => Number(row.share) || 0));
    const cap = max > 0.4 + 1e-9 ? 0.55 : 0.4;
    return max > cap ? Math.min(1, max + 0.05) : cap;
  };

  // Scales a recipe may use under the share cap; 0.5× flagged overShare when even that is too much.
  const feasibleSteps = (ctx, recipe) => {
    const limit = shareLimit(ctx);
    const steps = STEPS.filter((s) => !(limit > 0) || Math.round(recipe.kcal * s) <= limit);
    return steps.length ? { steps, over: false } : { steps: [0.5], over: true };
  };

  // ---------- score ----------

  const nutritionScore = (ctx, totals) => {
    const { W, target } = ctx;
    let score = 0;
    if (target.kcal > 0) {
      const e = Math.abs(totals.kcal - target.kcal) / target.kcal;
      const over = Math.max(0, e - W.kcalTol);
      score += W.kcalQuad * over * over + W.kcalLin * e;
    }
    if (target.protein > 0) {
      const short = Math.max(0, target.protein - totals.protein) / target.protein;
      const over = Math.max(0, short - W.proteinTol);
      score += W.proteinLin * short + W.proteinQuad * over * over;
    }
    if (target.carbs > 0) score += (W.carbs * Math.abs(totals.carbs - target.carbs)) / target.carbs;
    if (target.fat > 0) score += (W.fat * Math.abs(totals.fat - target.fat)) / target.fat;
    return score;
  };

  // Recipe-level taste: likes, skips, cook time, popularity. Constant per run → cached on ctx.
  const tasteScore = (ctx, recipe) => {
    ctx.tasteCache = ctx.tasteCache || new Map();
    const key = `${recipe.slot}|${recipe.id}`;
    if (ctx.tasteCache.has(key)) return ctx.tasteCache.get(key);
    const { W } = ctx;
    let score = 0;
    if (ctx.likes.has(recipe.id)) {
      const base = recipe.slot === 'main' ? W.likeMain : W.likeOther;
      score -= base * (ctx.fav?.like ?? 1) * (ctx.likeWeight?.get(recipe.id) || 1);
    }
    if (ctx.skips.has(recipe.id)) score += W.skip;
    if (recipe.slot === 'main' && ctx.recent?.has(recipe.id)) score += W.mainRecent;
    if (ctx.maxTime && recipe.time != null && recipe.time > ctx.maxTime) {
      score += W.timeOver + W.timePerMin * (recipe.time - ctx.maxTime);
    }
    const row = ctx.stats?.r?.[recipe.id];
    if (row && ctx.stats.avg) {
      score -= W.popularity * ((Number(row[6]) || 0) - (Number(ctx.stats.avg.like) || 0));
      score += W.popularity * ((Number(row[7]) || 0) - (Number(ctx.stats.avg.swap) || 0));
    }
    ctx.tasteCache.set(key, score);
    return score;
  };

  // Week context for variety: what the other days hold.
  // days: array of meal lists (index = day); the planned day's own slot is ignored.
  const weekState = (days, dayIndex) => {
    const counts = new Map();
    const byDay = (days || []).map((meals, index) => {
      const ids = new Set();
      const mis = new Set();
      if (index === dayIndex) return { ids, mis };
      (meals || []).forEach((meal) => {
        if (!meal?.id) return;
        ids.add(meal.id);
        if (meal.mi) mis.add(meal.mi);
        if (meal.slot === 'main') counts.set(meal.id, (counts.get(meal.id) || 0) + 1);
      });
      return { ids, mis };
    });
    return { counts, byDay, dayIndex };
  };

  const varietyScore = (ctx, recipe, ws) => {
    if (!ws) return 0;
    const { W } = ctx;
    const near = (offset) => ws.byDay[ws.dayIndex + offset];
    let score = 0;
    if (recipe.slot === 'main') {
      const used = ws.counts.get(recipe.id) || 0;
      if (used >= 1) score += W.mainRepeat;
      if (used >= MAIN_MAX) score += W.mainOverCap * (used - MAIN_MAX + 1);
      if (near(-1)?.ids.has(recipe.id) || near(1)?.ids.has(recipe.id)) score += W.mainAdjacent;
    } else {
      for (let back = 1; back <= ws.dayIndex; back += 1) {
        if (near(-back)?.ids.has(recipe.id)) score += W.otherYesterday / 2 ** (back - 1);
      }
      if (near(1)?.ids.has(recipe.id)) score += W.otherYesterday;
    }
    if (recipe.mi && (near(-1)?.mis.has(recipe.mi) || near(1)?.mis.has(recipe.mi))) score += W.ingredientAdjacent;
    return score;
  };

  const mealNutrition = (recipe, scale) => ({
    kcal: Math.round(recipe.kcal * scale),
    protein: Math.round(recipe.protein * scale),
    carbs: Math.round(recipe.carbs * scale),
    fat: Math.round(recipe.fat * scale),
  });

  // Full breakdown of a day: fixed meals (already scaled) + open meals { occasion, recipe, scale }.
  const scoreParts = (ctx, fixed, open, ws) => {
    const totals = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
    (fixed || []).forEach((meal) => {
      totals.kcal += Number(meal.kcal) || 0;
      totals.protein += Number(meal.protein) || 0;
      totals.carbs += Number(meal.carbs) || 0;
      totals.fat += Number(meal.fat) || 0;
    });
    const parts = { nutrition: 0, share: 0, taste: 0, variety: 0, portion: 0, ingredient: 0, overShare: 0 };
    const mis = new Map();
    (fixed || []).forEach((meal) => {
      if (meal.mi) mis.set(meal.mi, (mis.get(meal.mi) || 0) + 1);
    });
    const limit = shareLimit(ctx);
    open.forEach(({ occasion, recipe, scale }) => {
      const n = mealNutrition(recipe, scale);
      totals.kcal += n.kcal;
      totals.protein += n.protein;
      totals.carbs += n.carbs;
      totals.fat += n.fat;
      if (ctx.target.kcal > 0) {
        parts.share += (ctx.W.share * Math.abs(n.kcal - occasion.share * ctx.target.kcal)) / ctx.target.kcal;
      }
      parts.taste += tasteScore(ctx, recipe);
      parts.variety += varietyScore(ctx, recipe, ws);
      parts.portion += ctx.W.portion * Math.abs(scale - 1);
      if (limit > 0 && n.kcal > limit) parts.overShare += ctx.W.overShare;
      if (recipe.mi) mis.set(recipe.mi, (mis.get(recipe.mi) || 0) + 1);
    });
    mis.forEach((count) => {
      if (count > 1) parts.ingredient += ctx.W.ingredientSameDay * (count - 1);
    });
    parts.nutrition = nutritionScore(ctx, totals);
    const total = Object.values(parts).reduce((sum, v) => sum + v, 0);
    return { total, parts, totals };
  };

  // ---------- portions ----------

  const closestStep = (steps, kcal, ideal) => {
    let best = steps[0];
    steps.forEach((s) => {
      const d = Math.abs(kcal * s - ideal);
      const b = Math.abs(kcal * best - ideal);
      if (d < b - 1e-9 || (Math.abs(d - b) < 1e-9 && Math.abs(s - 1) < Math.abs(best - 1))) best = s;
    });
    return best;
  };

  const stepsFor = (ctx, recipe) => {
    ctx.stepCache = ctx.stepCache || new Map();
    const key = `${recipe.id}|${recipe.kcal}`;
    if (!ctx.stepCache.has(key)) ctx.stepCache.set(key, feasibleSteps(ctx, recipe).steps);
    return ctx.stepCache.get(key);
  };

  // Day prep shared by every combination: fixed totals, fixed main ingredients, per-recipe constants.
  const dayFrame = (ctx, fixed, ws) => {
    const base = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
    const mis = new Map();
    (fixed || []).forEach((meal) => {
      base.kcal += Number(meal.kcal) || 0;
      base.protein += Number(meal.protein) || 0;
      base.carbs += Number(meal.carbs) || 0;
      base.fat += Number(meal.fat) || 0;
      if (meal.mi) mis.set(meal.mi, (mis.get(meal.mi) || 0) + 1);
    });
    const constants = new Map();
    const constantOf = (recipe) => {
      if (!constants.has(recipe.id)) constants.set(recipe.id, tasteScore(ctx, recipe) + varietyScore(ctx, recipe, ws));
      return constants.get(recipe.id);
    };
    return { base, mis, constantOf, limit: shareLimit(ctx) };
  };

  // Coordinate descent over ¼ steps, starting at each meal's share of the target.
  // Same score as scoreParts, computed with plain arrays (this is the hot loop).
  const solvePortions = (ctx, frame, picks) => {
    const { W, target } = ctx;
    const n = picks.length;
    const steps = picks.map(({ recipe }) => stepsFor(ctx, recipe));
    const at = picks.map(({ occasion, recipe }, i) =>
      steps[i].indexOf(closestStep(steps[i], recipe.kcal, occasion.share * target.kcal))
    );
    let constant = 0;
    const mis = new Map(frame.mis);
    picks.forEach(({ recipe }) => {
      constant += frame.constantOf(recipe);
      if (recipe.mi) mis.set(recipe.mi, (mis.get(recipe.mi) || 0) + 1);
    });
    mis.forEach((count) => {
      if (count > 1) constant += W.ingredientSameDay * (count - 1);
    });
    const evaluate = () => {
      const totals = { kcal: frame.base.kcal, protein: frame.base.protein, carbs: frame.base.carbs, fat: frame.base.fat };
      let score = constant;
      for (let i = 0; i < n; i += 1) {
        const { occasion, recipe } = picks[i];
        const scale = steps[i][at[i]];
        const kcal = Math.round(recipe.kcal * scale);
        totals.kcal += kcal;
        totals.protein += Math.round(recipe.protein * scale);
        totals.carbs += Math.round(recipe.carbs * scale);
        totals.fat += Math.round(recipe.fat * scale);
        if (target.kcal > 0) score += (W.share * Math.abs(kcal - occasion.share * target.kcal)) / target.kcal;
        score += W.portion * Math.abs(scale - 1);
        if (frame.limit > 0 && kcal > frame.limit) score += W.overShare;
      }
      return score + nutritionScore(ctx, totals);
    };
    let current = evaluate();
    for (let round = 0; round < 16; round += 1) {
      let bestIndex = -1;
      let bestAt = -1;
      let bestScore = current - 1e-9;
      for (let i = 0; i < n; i += 1) {
        const from = at[i];
        for (let d = -1; d <= 1; d += 2) {
          const next = from + d;
          if (next < 0 || next >= steps[i].length) continue;
          at[i] = next;
          const score = evaluate();
          at[i] = from;
          if (score < bestScore) {
            bestScore = score;
            bestIndex = i;
            bestAt = next;
          }
        }
      }
      if (bestIndex < 0) break;
      at[bestIndex] = bestAt;
      current = bestScore;
    }
    return {
      open: picks.map(({ occasion, recipe }, i) => ({ occasion, recipe, scale: steps[i][at[i]] })),
      score: current,
    };
  };

  // ---------- day ----------

  const fixedMeal = (meal) => ({ ...meal, id: productId(meal.id), locked: true });

  const toMeal = (ctx, { occasion, recipe, scale }) => {
    const n = mealNutrition(recipe, scale);
    const limit = shareLimit(ctx);
    return {
      occasion: occasion.id,
      slot: recipe.slot,
      id: recipe.id,
      mi: recipe.mi,
      scale,
      ...n,
      liked: ctx.likes.has(recipe.id),
      locked: false,
      overShare: limit > 0 && n.kcal > limit,
      card: recipe.card,
    };
  };

  const dayTotals = (meals) =>
    meals.reduce(
      (acc, meal) => ({
        kcal: acc.kcal + (Number(meal.kcal) || 0),
        protein: acc.protein + (Number(meal.protein) || 0),
        carbs: acc.carbs + (Number(meal.carbs) || 0),
        fat: acc.fat + (Number(meal.fat) || 0),
      }),
      { kcal: 0, protein: 0, carbs: 0, fat: 0 }
    );

  const orderByOccasion = (ctx, meals) => {
    const rank = new Map(ctx.occasions.map((row, index) => [row.id, index]));
    return meals.slice().sort((a, b) => (rank.get(a.occasion) ?? 99) - (rank.get(b.occasion) ?? 99));
  };

  // Single-meal fitness against the occasion's share of the day: ranks the shortlist.
  const preScore = (ctx, occasion, recipe, ws) => {
    const share = occasion.share || 1 / Math.max(1, ctx.occasions.length);
    const sub = {
      ...ctx,
      target: {
        kcal: ctx.target.kcal * share,
        protein: ctx.target.protein * share,
        carbs: ctx.target.carbs * share,
        fat: ctx.target.fat * share,
      },
    };
    const { steps } = feasibleSteps(ctx, recipe);
    const scale = closestStep(steps, recipe.kcal, sub.target.kcal);
    const nutrition = nutritionScore(sub, mealNutrition(recipe, scale)) * share;
    return nutrition + tasteScore(ctx, recipe) + varietyScore(ctx, recipe, ws) + ctx.W.portion * Math.abs(scale - 1);
  };

  // Bonus for a meal that is also tomorrow's lunch (Reste einplanen): Meal-Prep recipes reheat well.
  const prepBonus = (ctx, recipe, cookAhead) => (cookAhead && ctx.prep?.has(recipe.id) ? ctx.W.prepDinner : 0);

  const shortlist = (ctx, occasion, ws, blocked, rand, cookAhead) => {
    const pool = (ctx.catalog[occasion.slot] || []).filter((r) => !blocked.has(r.id));
    const ranked = pool
      .map((recipe) => ({ recipe, score: preScore(ctx, occasion, recipe, ws) - prepBonus(ctx, recipe, cookAhead) }))
      .sort((a, b) => a.score - b.score || a.recipe.id.localeCompare(b.recipe.id));
    const size = occasion.slot === 'main' ? SHORTLIST.main : SHORTLIST.other;
    const out = ranked.slice(0, size);
    const have = new Set(out.map((row) => row.recipe.id));
    ranked
      .filter((row) => !have.has(row.recipe.id) && ctx.likes.has(row.recipe.id))
      .slice(0, ctx.fav?.shortlist ?? SHORTLIST_LIKES)
      .forEach((row) => {
        out.push(row);
        have.add(row.recipe.id);
      });
    const rest = ranked.filter((row) => !have.has(row.recipe.id));
    for (let i = 0; i < SHORTLIST_EXPLORE && rest.length; i += 1) {
      out.push(rest.splice(Math.floor(rand() * rest.length), 1)[0]);
    }
    return out.map((row) => row.recipe);
  };

  // Ids a slot must not get back (most recent first). When the pool can't avoid all of them, the oldest are
  // allowed again first, so a small pool (3 breakfasts) rotates instead of ping-ponging between two.
  const forbiddenFor = (ctx, occasion, dayIds, list) => {
    const ids = [].concat(list || []).map(productId).filter(Boolean);
    const pool = (ctx.catalog[occasion.slot] || []).filter((r) => !dayIds.has(r.id));
    for (let n = ids.length; n > 0; n -= 1) {
      const blocked = new Set(ids.slice(0, n));
      if (pool.some((r) => !blocked.has(r.id))) return blocked;
    }
    return new Set();
  };

  // opts: { seed, dayIndex, days (meal lists of the whole week for variety), fixed (locked meals),
  //         forbid ({ [occasionId]: id | id[] } — remix never puts back what was there or was shown recently),
  //         pick ({ band, temperature } — how far from the best day the seeded pick may go),
  //         pin ({ [occasionId]: id } — this recipe, portion solved with the day: yesterday's dinner as today's lunch),
  //         cookAhead ([occasionId] — meals that also feed tomorrow: Meal-Prep recipes preferred) }
  const planDay = (ctx, opts) => {
    const rand = rng(opts?.seed ?? 0);
    const dayIndex = opts?.dayIndex ?? 0;
    const ws = weekState(opts?.days, dayIndex);
    const fixed = (opts?.fixed || []).map(fixedMeal);
    const fixedOccasions = new Set(fixed.map((meal) => meal.occasion));
    const open = ctx.occasions.filter((occasion) => !fixedOccasions.has(occasion.id));
    const dayIds = new Set(fixed.map((meal) => meal.id));
    const forbid = opts?.forbid || {};
    const pick = { ...DAY_PICK, ...(opts?.pick || {}) };
    const hasForbid = open.some((occasion) => [].concat(forbid[occasion.id] || []).length);
    const pin = opts?.pin || {};
    const cookAhead = new Set(opts?.cookAhead || []);
    let kept = searchDay(ctx, { rand, ws, fixed, open, dayIds, forbid, pin, cookAhead });
    // Lunch and dinner share one pool: forbids can leave no valid pair even when each slot alone has options.
    if (!kept.length && hasForbid && open.length) kept = searchDay(ctx, { rand, ws, fixed, open, dayIds, forbid: {}, pin, cookAhead });

    const best = kept.length ? kept[0].score : 0;
    const band = kept.filter((row) => row.score <= best + pick.band);
    const chosen = softmaxPick(band, (row) => row.score, pick.temperature, rand);
    const meals = orderByOccasion(ctx, [...fixed, ...(chosen ? chosen.open.map((m) => toMeal(ctx, m)) : [])]);
    return { meals, totals: dayTotals(meals), score: chosen ? chosen.score : scoreParts(ctx, fixed, [], ws).total };
  };

  // Shortlists, every combination, portions per combination → the KEEP_DAYS best distinct days.
  const searchDay = (ctx, { rand, ws, fixed, open, dayIds, forbid, pin = {}, cookAhead = new Set() }) => {
    const lists = open.map((occasion) => {
      const pinned = pin[occasion.id] && (ctx.catalog[occasion.slot] || []).find((r) => r.id === productId(pin[occasion.id]));
      if (pinned && !dayIds.has(pinned.id)) return { occasion, recipes: [pinned] };
      const blocked = new Set([...dayIds, ...forbiddenFor(ctx, occasion, dayIds, forbid[occasion.id])]);
      return { occasion, recipes: shortlist(ctx, occasion, ws, blocked, rand, cookAhead.has(occasion.id)) };
    });

    const frame = dayFrame(ctx, fixed, ws);
    const kept = [];
    const consider = (picks) => {
      const solved = solvePortions(ctx, frame, picks);
      picks.forEach(({ occasion, recipe }) => {
        solved.score -= prepBonus(ctx, recipe, cookAhead.has(occasion.id));
      });
      const key = solved.open.map((m) => `${m.recipe.id}@${m.scale}`).join(',');
      if (kept.some((row) => row.key === key)) return;
      if (kept.length < KEEP_DAYS || solved.score < kept[kept.length - 1].score) {
        kept.push({ key, score: solved.score, open: solved.open });
        kept.sort((a, b) => a.score - b.score || a.key.localeCompare(b.key));
        if (kept.length > KEEP_DAYS) kept.pop();
      }
    };

    const active = lists.filter((row) => row.recipes.length);
    const walk = (index, picks, used) => {
      if (index === active.length) {
        consider(picks);
        return;
      }
      const { occasion, recipes } = active[index];
      recipes.forEach((recipe) => {
        if (used.has(recipe.id)) return;
        used.add(recipe.id);
        picks.push({ occasion, recipe });
        walk(index + 1, picks, used);
        picks.pop();
        used.delete(recipe.id);
      });
    };
    walk(0, [], new Set(dayIds));
    return kept;
  };

  // Reste einplanen: a dinner also covers the lunch of the next `span` days (same recipe, own portion each):
  // span 1 = twice from one cook, span 2 = three times. Monday's lunch is cooked.
  // A leftover lunch says where it comes from: leftover true = yesterday's dinner, 2 = the dinner two days ago.
  const LEFTOVER = { from: 'dinner', to: 'lunch' };
  const leftoverSpan = (value) => (Number(value) === 2 ? 2 : value ? 1 : 0);
  const leftoverBack = (meal) => (meal?.leftover === 2 ? 2 : meal?.leftover ? 1 : 0);

  // days: meal lists of the week (index = day, leftover flags kept); fixedToday: today's locked meals.
  // → { id, back } (the dinner `back` days ago this lunch is made from) or null.
  const leftoverSource = (ctx, days, dayIndex, fixedToday, span = 1) => {
    const occasions = new Set(ctx.occasions.map((row) => row.id));
    if (!occasions.has(LEFTOVER.from) || !occasions.has(LEFTOVER.to)) return null;
    if ((fixedToday || []).some((meal) => meal.occasion === LEFTOVER.to)) return null;
    const meal = (index, occasion) => (days?.[index] || []).find((row) => row.occasion === occasion);
    // A batch that fed yesterday's lunch lasts one more day.
    if (leftoverSpan(span) >= 2 && dayIndex >= 2) {
      const prevLunch = meal(dayIndex - 1, LEFTOVER.to);
      const old = meal(dayIndex - 2, LEFTOVER.from);
      if (leftoverBack(prevLunch) === 1 && old?.id && productId(prevLunch.id) === productId(old.id) && !isCustomId(old.id)) {
        return { id: productId(old.id), back: 2 };
      }
    }
    if (dayIndex < 1) return null;
    const dinner = meal(dayIndex - 1, LEFTOVER.from);
    return dinner?.id && !isCustomId(dinner.id) ? { id: productId(dinner.id), back: 1 } : null;
  };

  // Does this day's dinner cook for tomorrow? Not when today's lunch starts a two-day batch: tomorrow's lunch
  // continues it, so tonight's dinner is just dinner.
  const cooksAhead = (span, source) => !(leftoverSpan(span) >= 2 && source?.back === 1);

  // opts: { seed, days = 7, fixed: Meal[][] (locked per day), forbid: [{ [occasionId]: id | id[] }] per day, pick,
  //         leftovers (Reste einplanen: true / 1 = one lunch per dinner, 2 = two lunches) }
  // Days are planned in order; each sees the days before it (and fixed meals of the days after).
  // A generator, so a browser caller can yield between days (one day is the unit of work).
  function* planWeekSteps(ctx, opts) {
    const length = opts?.days ?? 7;
    const fixed = opts?.fixed || [];
    const span = leftoverSpan(opts?.leftovers);
    const known = Array.from({ length }, (_, index) => (fixed[index] || []).map(fixedMeal));
    for (let index = 0; index < length; index += 1) {
      const source = span ? leftoverSource(ctx, known, index, fixed[index], span) : null;
      const day = planDay(ctx, {
        seed: seed(opts?.seed ?? 0, 'day', index),
        dayIndex: index,
        days: known,
        fixed: fixed[index] || [],
        forbid: opts?.forbid?.[index],
        pick: opts?.pick,
        pin: source ? { [LEFTOVER.to]: source.id } : undefined,
        cookAhead: span && index < length - 1 && cooksAhead(span, source) ? [LEFTOVER.from] : undefined,
      });
      if (source) {
        day.meals = day.meals.map((meal) =>
          meal.occasion === LEFTOVER.to && meal.id === source.id ? { ...meal, leftover: source.back === 2 ? 2 : true } : meal
        );
      }
      known[index] = day.meals;
      yield day;
    }
  }

  const planWeek = (ctx, opts) => [...planWeekSteps(ctx, opts)];

  // Remix history of one slot (most recent first, deduped, capped): the forbid list for the next remix.
  const REMIX_HISTORY_MAX = 6;
  const rememberShown = (list, id, max = REMIX_HISTORY_MAX) => {
    const pid = productId(id);
    const rest = (list || []).filter((x) => x !== pid);
    return (pid ? [pid, ...rest] : rest).slice(0, max);
  };

  // ---------- swap ----------

  // Every candidate scored as "this day with that recipe in this meal" (other meals stay as they are).
  // opts: { days (meal lists of the week), dayIndex, occasionId, pool? (cards; default: the slot's catalog),
  //         exclude? (ids) }. Same-day repeats are never returned.
  const rankSwaps = (ctx, opts) => {
    const dayIndex = opts?.dayIndex ?? 0;
    const days = (opts?.days || []).map((meals) => (meals || []).map((meal) => ({ ...meal, id: productId(meal.id) })));
    const today = days[dayIndex] || [];
    const current = today.find((meal) => meal.occasion === opts?.occasionId);
    const occasion = ctx.occasions.find((row) => row.id === opts?.occasionId) || {
      id: opts?.occasionId,
      slot: current?.slot || 'main',
      share: 1 / Math.max(1, ctx.occasions.length),
    };
    const others = today.filter((meal) => meal.occasion !== occasion.id);
    days[dayIndex] = others;
    const ws = weekState(days, dayIndex);
    const blocked = new Set([...others.map((meal) => meal.id), ...toIdSet(opts?.exclude)]);
    const pool = opts?.pool
      ? opts.pool.filter(hasMacros).map((card) => toRecipe(card, occasion.slot))
      : ctx.catalog[occasion.slot] || [];
    const rows = [];
    pool.forEach((recipe) => {
      if (!recipe.id || blocked.has(recipe.id)) return;
      const { steps } = feasibleSteps(ctx, recipe);
      let best = null;
      steps.forEach((scale) => {
        const score = scoreParts(ctx, others, [{ occasion, recipe, scale }], ws).total;
        if (!best || score < best.score - 1e-9) best = { scale, score };
      });
      rows.push({ ...toMeal(ctx, { occasion, recipe, scale: best.scale }), recipe, score: best.score });
    });
    return rows.sort((a, b) => a.score - b.score || a.id.localeCompare(b.id));
  };

  // Seeded pick from the best few swaps; never something already tried in this sheet, and
  // preferably nothing else in the week.
  const surprise = (ctx, opts) => {
    const rows = rankSwaps(ctx, opts);
    const tried = toIdSet(opts?.tried);
    const weekIds = new Set();
    (opts?.days || []).forEach((meals, index) =>
      (meals || []).forEach((meal) => {
        if (index === opts?.dayIndex && meal.occasion === opts?.occasionId) return;
        weekIds.add(productId(meal.id));
      })
    );
    let candidates = rows.filter((row) => !tried.has(row.id) && !weekIds.has(row.id));
    if (!candidates.length) candidates = rows.filter((row) => !tried.has(row.id));
    candidates = candidates.slice(0, SURPRISE_PICK.top);
    return softmaxPick(candidates, (row) => row.score, SURPRISE_PICK.temperature, rng(opts?.seed ?? 0));
  };

  // Portion for one recipe placed into a meal (swap pick, In Plan einfügen, Eigenes Gericht).
  const bestPortion = (ctx, opts) => {
    const [row] = rankSwaps(ctx, { ...opts, pool: [opts.recipe], exclude: [] });
    return row ? row.scale : 1;
  };

  const scoreDay = (ctx, meals, opts) => {
    const ws = weekState(opts?.days, opts?.dayIndex ?? 0);
    const fixed = (meals || []).filter((meal) => meal.locked || !meal.card);
    const open = (meals || [])
      .filter((meal) => !(meal.locked || !meal.card))
      .map((meal) => ({
        occasion: ctx.occasions.find((row) => row.id === meal.occasion) || { id: meal.occasion, share: 0 },
        recipe: toRecipe(meal.card, meal.slot),
        scale: meal.scale,
      }));
    return scoreParts(ctx, fixed, open, ws);
  };

  // ---------- swipe deck ----------

  // Per-slot deck order: assets/vhp-deck-order.js, shared with the homepage feed. opts.kind: main ingredient,
  // opts.taste: likes / skips / goal for the personal tilt.
  const deckOrder = (ctx, opts) => {
    const order = globalThis.vhpDeckOrder || (typeof require === 'function' ? require('./vhp-deck-order.js') : null);
    return order.rank(opts?.cards, { stats: ctx?.stats, seed: opts?.seed, now: opts?.now, kind: opts?.kind, taste: opts?.taste });
  };

  return {
    STEPS,
    LEFTOVER,
    leftoverSpan,
    leftoverBack,
    leftoverSource,
    cooksAhead,
    WEIGHTS,
    FAV_MIX,
    FREQ_WEIGHT,
    MAIN_MAX,
    REMIX_PICK,
    REMIX_HISTORY_MAX,
    rememberShown,
    productId,
    isCustomId,
    seed,
    rng,
    context,
    shareLimit,
    MEAL_SIZES,
    mealShares,
    fitCount,
    shareCapFor,
    feasibleSteps,
    nutritionScore,
    scoreDay,
    planDay,
    planWeek,
    planWeekSteps,
    rankSwaps,
    surprise,
    bestPortion,
    deckOrder,
  };
});
